import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ingredientService } from '../../services/ingredientService';
import { Ingredient } from '../../types/ingredient';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import IngredientModal from '../../components/Setup/IngredientModal';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import { useToast } from '../../components/UI/ToastContainer';
import DataGrid from '../../components/DataGrid';
import { ColumnDef } from '@tanstack/react-table';

const IngredientsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [ingredientToDelete, setIngredientToDelete] = useState<Ingredient | null>(null);

  const pageSize = 50;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ name?: string; status?: string; ingredientFamilyId?: string }>({});
  const [sort, setSort] = useState<{ column: string; order: 'asc' | 'desc' }>({ column: 'name', order: 'asc' });

  const { column: sortBy, order: sortOrder } = sort;


  // Fetch ingredients
  const { data: ingredientsData, isLoading, error } = useQuery({
    queryKey: ['ingredients', currentPage, searchTerm, sortBy, sortOrder, filters],
    queryFn: () => ingredientService.getIngredients({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      name: filters.name || undefined,
      status: filters.status || undefined,
      ingredientFamilyId: filters.ingredientFamilyId || undefined,
      sortBy,
      sortOrder,
    }),
  });

  // Fetch ingredient families for dropdown
  const { data: familiesData } = useQuery({
    queryKey: ['ingredient-families'],
    queryFn: () => ingredientService.getIngredientFamilies({ limit: 1000 }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => ingredientService.deleteIngredient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      setIsDeleteModalOpen(false);
      setIngredientToDelete(null);
      showSuccess('Ingredient deleted', 'The ingredient has been successfully deleted.');
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to delete ingredient. Please try again.';
      showError('Delete failed', message);
    },
  });

  const handleEdit = useCallback((ingredient: Ingredient) => {
    setSelectedIngredient(ingredient);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback((ingredient: Ingredient) => {
    setIngredientToDelete(ingredient);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedIngredient(null);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedIngredient(null);
  }, []);

  const handleModalSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['ingredients'] });
    handleModalClose();
  }, [handleModalClose, queryClient]);

  const handleExport = async () => {
    try {
      const blob = await ingredientService.exportIngredients({
        search: searchTerm,
        name: filters.name,
        status: filters.status,
        ingredientFamilyId: filters.ingredientFamilyId,
        ids: selectedIds.length ? selectedIds : undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `ingredients-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };
  const toggleRow = useCallback((id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const handleBulkAction = useCallback(async (action: 'activate' | 'deactivate') => {
    try {
      if (!selectedIds.length) return;
      const status = action === 'activate' ? 'ACTIVE' : 'INACTIVE';
      await ingredientService.bulkUpdateIngredientStatus(selectedIds, status);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
    } catch (e) {
      console.error('Bulk update failed', e);
    }
  }, [queryClient, selectedIds]);
  const ingredients = ingredientsData?.ingredients || [];
  const pagination = ingredientsData?.pagination;

  const columns = useMemo<ColumnDef<Ingredient>[]>(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Description',
      meta: { sortable: true },
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'code',
      accessorKey: 'code',
      header: 'Code',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'family',
      header: 'Family',
      cell: ({ row }) => row.original.ingredientFamily?.name || '-',
    },
    {
      id: 'status',
      header: 'Status',
      meta: { sortable: true },
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.original.status === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'statusDate',
      header: 'Status Date',
      cell: ({ row }) => (row.original.statusDate ? new Date(row.original.statusDate).toLocaleString() : '-'),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => handleEdit(row.original)}
            className="text-primary-600 hover:text-primary-900"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row.original)}
            className="text-red-600 hover:text-red-900"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], [handleDelete, handleEdit]);

  const handleSortChange = (columnId: string) => {
    setSort((prev) => {
      if (prev.column === columnId) {
        return { column: columnId, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { column: columnId, order: 'asc' };
    });
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Error loading ingredients. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Ingredients</h1>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button
            variant="secondary"
            disabled={selectedIds.length === 0}
            onClick={() => handleBulkAction('activate')}
          >
            Activate
          </Button>
          <Button
            variant="secondary"
            disabled={selectedIds.length === 0}
            onClick={() => handleBulkAction('deactivate')}
          >
            Deactivate
          </Button>
          <Button onClick={handleCreate}>
            <PlusIcon className="w-5 h-5 mr-2" />
            Create
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search ingredients..."
              className="pl-10 form-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-soft">
        <div className="p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input
                type="text"
                className="form-input w-full text-sm"
                placeholder="Filter name..."
                value={filters.name || ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, name: e.target.value || undefined }));
                  setCurrentPage(1);
                }}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Family</label>
              <select
                className="form-select w-full text-sm"
                value={filters.ingredientFamilyId || ''}
                onChange={(e) => {
                  const v = e.target.value || undefined;
                  setFilters((f) => ({ ...f, ingredientFamilyId: v }));
                  setCurrentPage(1);
                }}
              >
                <option value="">All families</option>
                {(familiesData?.ingredientFamilies || []).map((family) => (
                  <option key={family.id} value={family.id}>
                    {family.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="form-select w-full text-sm"
                value={filters.status || ''}
                onChange={(e) => {
                  const v = e.target.value || undefined;
                  setFilters((f) => ({ ...f, status: v }));
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFilters({});
                  setSearchTerm('');
                  setSelectedIds([]);
                  setCurrentPage(1);
                }}
              >
                Clear filters
              </Button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <DataGrid
              data={ingredients}
              columns={columns}
              getRowId={(row) => row.id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={(id) => toggleRow(id)}
              onToggleAll={(ids, checked) =>
                setSelectedIds((prev) =>
                  checked
                    ? Array.from(new Set([...prev, ...ids]))
                    : prev.filter((id) => !ids.includes(id))
                )
              }
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          )}
        </div>

        {pagination && (
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>{' '}
              to{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span>{' '}
              of{' '}
              <span className="font-medium">{pagination.total}</span>{' '}
              results
            </p>
            <div className="space-x-2">
              <Button
                variant="secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                disabled={pagination && currentPage === pagination.pages}
                onClick={() => setCurrentPage((p) => (pagination ? Math.min(pagination.pages, p + 1) : p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {isModalOpen && (
        <IngredientModal
          ingredient={selectedIngredient}
          ingredientFamilies={familiesData?.ingredientFamilies || []}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {isDeleteModalOpen && ingredientToDelete && (
        <DeleteConfirmModal
          title="Delete Ingredient"
          message={`Are you sure you want to delete "${ingredientToDelete.description}"? This action cannot be undone.`}
          onConfirm={() => deleteMutation.mutate(ingredientToDelete.id)}
          onCancel={() => {
            setIsDeleteModalOpen(false);
            setIngredientToDelete(null);
          }}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default IngredientsPage;
